import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useMutation } from '@apollo/client/react'
import * as Sentry from '@sentry/react'
import { useCookie } from '@buffer-mono/popcorn'
import type { Theme } from '@buffer-mono/popcorn'

import { HEARD_ABOUT_PROMPT_SESSION_KEY } from '~publish/components/HeardAboutPrompt/HeardAboutPrompt'
import { useOrganizationId } from '~publish/contexts/AccountContext/hooks'
import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'

export const INITIATE_ONBOARDING = graphql(/* GraphQL */ `
  mutation InitiateOnboarding($input: InitiateOnboardingInput!) {
    initiateOnboarding(input: $input) {
      __typename
      ... on InitiateOnboardingResponse {
        step {
          __typename
          ... on IntroductionStep {
            slug
            title
            subtitle
            buttonText
            isSkippable
          }
          ... on SurveyStep {
            slug
            surveyStepType
            title
            description
            isSkippable
            options {
              slug
              label
              description
            }
          }
          ... on TourStep {
            slug
            title
            description
            isSkippable
            items {
              slug
              title
              description
              link
              linkTitle
            }
          }
        }
        status
        flowSlug
        version
        existingSelectedOptions
        totalSurveySteps
      }
      ... on OnboardingAlreadyCompletedError {
        message
      }
    }
  }
`)

export const COMPLETE_ONBOARDING_STEP = graphql(/* GraphQL */ `
  mutation CompleteOnboardingStep($input: CompleteOnboardingStepInput!) {
    completeOnboardingStep(input: $input) {
      __typename
      ... on CompleteOnboardingStepResponse {
        nextStep {
          __typename
          ... on IntroductionStep {
            slug
            title
            subtitle
            buttonText
            isSkippable
          }
          ... on SurveyStep {
            slug
            surveyStepType
            title
            description
            isSkippable
            options {
              slug
              label
              description
            }
          }
          ... on TourStep {
            slug
            title
            description
            isSkippable
            items {
              slug
              title
              description
              link
              linkTitle
            }
          }
        }
        status
        existingSelectedOptions
        existingTextValue
        flowSlug
        version
      }
      ... on OnboardingAlreadyCompletedError {
        message
      }
    }
  }
`)

type InitiateOnboardingResponse = Extract<
  ResultOf<typeof INITIATE_ONBOARDING>['initiateOnboarding'],
  { __typename: 'InitiateOnboardingResponse' }
>

/** A single onboarding step — the introduction, a survey question, or the
 * feature tour — as returned by the onboarding flow mutations. */
export type OnboardingStep = NonNullable<InitiateOnboardingResponse['step']>

export type SurveyStep = Extract<OnboardingStep, { __typename: 'SurveyStep' }>
export type TourStep = Extract<OnboardingStep, { __typename: 'TourStep' }>
export type SurveyStepOption = SurveyStep['options'][number]
export type SurveyStepType = SurveyStep['surveyStepType']

const THEME_COOKIE_KEY = 'popcorn_theme'

interface UseOnboardingFlowReturn {
  step: OnboardingStep | null
  stepHistory: OnboardingStep[]
  totalSurveySteps: number
  initialAnswers: string[]
  initialTextValue?: string
  loading: boolean
  skipAllLoading: boolean
  initialized: boolean
  canGoBack: boolean
  error: string | null
  clearError: () => void
  flowSlug: string | null
  flowVersion: number | null
  submitStep: (answers: string[], textValue?: string) => Promise<void>
  skipStep: () => Promise<void>
  skipAll: () => Promise<void>
  goBack: () => void
}

export function useOnboardingFlow(): UseOnboardingFlowReturn {
  const organizationId = useOrganizationId()
  const navigate = useNavigate()

  const [step, setStep] = useState<OnboardingStep | null>(null)
  const [initialAnswers, setInitialAnswers] = useState<string[]>([])
  const [initialTextValue, setInitialTextValue] = useState<string | undefined>()
  const [initialized, setInitialized] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [stepHistory, setStepHistory] = useState<OnboardingStep[]>([])
  const [answersMap, setAnswersMap] = useState<Record<string, string[]>>({})
  const [textValueMap, setTextValueMap] = useState<Record<string, string>>({})
  const [flowSlug, setFlowSlug] = useState<string | null>(null)
  const [flowVersion, setFlowVersion] = useState<number | null>(null)
  const [totalSurveySteps, setTotalSurveySteps] = useState(0)
  const [skipAllLoading, setSkipAllLoading] = useState(false)
  const skipAllInProgress = useRef(false)
  const [themeCookie, setThemeCookie] = useCookie<Theme | null>(
    THEME_COOKIE_KEY,
    null,
  )

  const [initiateOnboarding, { loading: initiateLoading }] =
    useMutation(INITIATE_ONBOARDING)
  const [completeOnboardingStep, { loading: completeLoading }] = useMutation(
    COMPLETE_ONBOARDING_STEP,
  )

  const loading = initiateLoading || completeLoading || skipAllLoading

  const exitCompletedFlow = useCallback(
    (completedFlowSlug: string | null, completedFlowVersion: number | null) => {
      sessionStorage.setItem(HEARD_ABOUT_PROMPT_SESSION_KEY, 'true')
      sessionStorage.setItem(
        'heardAboutPrompt_flowSlug',
        completedFlowSlug ?? '',
      )
      sessionStorage.setItem(
        'heardAboutPrompt_flowVersion',
        String(completedFlowVersion ?? 0),
      )
      navigate('/', { replace: true })
    },
    [navigate],
  )

  // Establish 'system' as the default appearance preference as early as
  // possible so other .buffer.com surfaces (e.g. account-frontend, which mounts
  // PopcornProvider with theme="light") see an existing cookie and can't clobber
  // the shared preference with a resolved value. An explicit toggle during
  // onboarding still overwrites this via setTheme.
  useEffect(() => {
    if (!themeCookie) {
      setThemeCookie('system')
    }
    // Run once on mount: we only want to seed the default when no preference
    // exists yet, not react to later cookie changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    async function initiate(): Promise<void> {
      try {
        if (!organizationId) return

        setError(null)
        const { data } = await initiateOnboarding({
          variables: {
            input: { organizationId },
          },
        })

        const result = data?.initiateOnboarding
        if (!result) return

        if (result.__typename === 'InitiateOnboardingResponse') {
          setStep(result.step)
          setInitialAnswers(result.existingSelectedOptions ?? [])
          setFlowSlug(result.flowSlug ?? null)
          setFlowVersion(result.version ?? null)
          setTotalSurveySteps(result.totalSurveySteps)
          setInitialized(true)
        } else {
          navigate('/', { replace: true })
        }
      } catch {
        navigate('/', { replace: true })
      }
    }

    void initiate()
  }, [organizationId, initiateOnboarding, navigate])

  const completeStep = useCallback(
    async (options?: {
      selectedOptions?: string[]
      textValue?: string
    }): Promise<void> => {
      if (!organizationId || !step) return

      const currentStep = step

      setError(null)
      try {
        const { data } = await completeOnboardingStep({
          variables: {
            input: {
              organizationId,
              stepSlug: currentStep.slug,
              selectedOptions: options?.selectedOptions,
              skipped: !options?.selectedOptions,
              textValue: options?.textValue,
            },
          },
        })

        const result = data?.completeOnboardingStep
        if (result?.__typename === 'CompleteOnboardingStepResponse') {
          if (result.status === 'completed') {
            exitCompletedFlow(result.flowSlug, result.version)
            return
          }
          setStepHistory((prev) => [...prev, currentStep])
          if (options?.selectedOptions) {
            const answers = options.selectedOptions
            setAnswersMap((prev) => ({
              ...prev,
              [currentStep.slug]: answers,
            }))
          }
          if (options?.textValue != null) {
            const text = options.textValue
            setTextValueMap((prev) => ({
              ...prev,
              [currentStep.slug]: text,
            }))
          }
          setStep(result.nextStep ?? null)
          setInitialAnswers(result.existingSelectedOptions ?? [])
          setInitialTextValue(result.existingTextValue ?? undefined)
        } else if (result?.__typename === 'OnboardingAlreadyCompletedError') {
          navigate('/', { replace: true })
        }
      } catch {
        setError('Something went wrong. Please try again.')
      }
    },
    [organizationId, step, completeOnboardingStep, navigate, exitCompletedFlow],
  )

  const submitStep = useCallback(
    async (answers: string[], textValue?: string): Promise<void> => {
      await completeStep({ selectedOptions: answers, textValue })
    },
    [completeStep],
  )

  const skipStep = useCallback(async (): Promise<void> => {
    await completeStep()
  }, [completeStep])

  const skipAll = useCallback(async (): Promise<void> => {
    if (!organizationId || !step || skipAllInProgress.current) return

    let stepToSkip: OnboardingStep | null = step
    let nextInitialAnswers = initialAnswers
    let nextInitialTextValue = initialTextValue
    const skippedSteps: OnboardingStep[] = []

    skipAllInProgress.current = true
    setSkipAllLoading(true)
    setError(null)

    try {
      while (stepToSkip) {
        // Each response identifies the next step, so these requests cannot run in parallel.
        // eslint-disable-next-line no-await-in-loop
        const { data } = await completeOnboardingStep({
          variables: {
            input: {
              organizationId,
              stepSlug: stepToSkip.slug,
              selectedOptions: undefined,
              skipped: true,
              textValue: undefined,
            },
          },
        })

        const result = data?.completeOnboardingStep

        if (result?.__typename === 'OnboardingAlreadyCompletedError') {
          navigate('/', { replace: true })
          return
        }

        if (result?.__typename !== 'CompleteOnboardingStepResponse') {
          throw new Error('Unable to skip the remaining onboarding steps')
        }

        if (result.status === 'completed') {
          exitCompletedFlow(result.flowSlug, result.version)
          return
        }

        const nextStep = result.nextStep
        if (!nextStep) {
          throw new Error('Onboarding did not return the next step')
        }

        skippedSteps.push(stepToSkip)
        stepToSkip = nextStep
        nextInitialAnswers = result.existingSelectedOptions ?? []
        nextInitialTextValue = result.existingTextValue ?? undefined
      }
    } catch (skipAllError) {
      const skippedStepSlugs = new Set(
        skippedSteps.map((skippedStep) => skippedStep.slug),
      )

      setStepHistory((previousSteps) => [...previousSteps, ...skippedSteps])
      setAnswersMap((previousAnswers) =>
        Object.fromEntries(
          Object.entries(previousAnswers).filter(
            ([stepSlug]) => !skippedStepSlugs.has(stepSlug),
          ),
        ),
      )
      setTextValueMap((previousTextValues) =>
        Object.fromEntries(
          Object.entries(previousTextValues).filter(
            ([stepSlug]) => !skippedStepSlugs.has(stepSlug),
          ),
        ),
      )
      setStep(stepToSkip)
      setInitialAnswers(nextInitialAnswers)
      setInitialTextValue(nextInitialTextValue)
      setError('Something went wrong. Please try again.')
      Sentry.captureException(skipAllError)
    } finally {
      skipAllInProgress.current = false
      setSkipAllLoading(false)
    }
  }, [
    organizationId,
    step,
    initialAnswers,
    initialTextValue,
    completeOnboardingStep,
    navigate,
    exitCompletedFlow,
  ])

  const clearError = useCallback((): void => {
    setError(null)
  }, [])

  const goBack = useCallback((): void => {
    if (stepHistory.length === 0) return

    setError(null)
    const previousStep = stepHistory[stepHistory.length - 1]
    setStepHistory((prev) => prev.slice(0, -1))
    setStep(previousStep)
    setInitialAnswers(answersMap[previousStep.slug] ?? [])
    setInitialTextValue(textValueMap[previousStep.slug])
  }, [stepHistory, answersMap, textValueMap])

  return {
    step,
    stepHistory,
    totalSurveySteps,
    initialAnswers,
    initialTextValue,
    loading,
    skipAllLoading,
    initialized,
    canGoBack: stepHistory.length > 0,
    error,
    clearError,
    flowSlug,
    flowVersion,
    submitStep,
    skipStep,
    skipAll,
    goBack,
  }
}
